"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { track } from "@/lib/observability/analytics";

type Props = {
  packSlug?: string;
  procedureSlug?: string;
  className?: string;
};

const COUNTRY_CODES = ["FR", "BE", "NL", "ES", "IT", "DE", "UK", "CA", "US", "CH", "OTH"] as const;

export function MrePackRequestForm({ packSlug, procedureSlug, className = "" }: Props) {
  const [pending, setPending] = useState(false);
  const t = useTranslations("forms.mrePack");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = { ...Object.fromEntries(form.entries()), packSlug, procedureSlug };
    setPending(true);
    try {
      const res = await fetch("/api/mre/pack-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message ?? t("successDefault"));
        (e.target as HTMLFormElement).reset();
        track("mre:quote_requested", { packSlug, procedureSlug });
      } else {
        toast.error(data.message ?? t("errorGeneric"));
      }
    } catch {
      toast.error(t("errorNetwork"));
    } finally {
      setPending(false);
    }
  }

  return (
    <form name="mre-pack-request" onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <input name="firstName" required placeholder={t("firstNamePlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
        <input name="lastName" required placeholder={t("lastNamePlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input name="email" type="email" required placeholder={t("emailPlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
        <input name="phone" type="tel" required placeholder={t("phonePlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <select name="countryFrom" required className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4">
          <option value="">{t("countryFromPlaceholder")}</option>
          {COUNTRY_CODES.map((code) => (
            <option key={code} value={code}>
              {t(`countries.${code}`)}
            </option>
          ))}
        </select>
        <input name="preferredCity" placeholder={t("preferredCityPlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input name="preferredPeriod" placeholder={t("preferredPeriodPlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
        <input name="budgetMad" type="number" min="0" placeholder={t("budgetMadPlaceholder")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4" />
      </div>
      <textarea name="message" rows={4} placeholder={t("messagePlaceholder")} className="w-full rounded-xl border border-[var(--sahha-border-default)] bg-white p-4" />
      <button type="submit" disabled={pending} className="inline-flex h-12 items-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-6 font-semibold text-white hover:bg-[var(--sahha-primary-700)] disabled:opacity-60">
        {pending ? t("submitting") : t("submit")}
      </button>
    </form>
  );
}
